'use client'

import { useState, useEffect } from 'react'
import s from './testimonials.module.css'

export type Testimonial = {
  text: string
  author: string
  source: 'google' | 'manuel'
  rating?: number
  url?: string
}

type Props = {
  testimonials: Testimonial[]
  rating?: number
  count?: number
  mapsUrl?: string
}

const DELAY = 6000      // rotation automatique : toutes les 6 secondes
const MAX_CHARS = 260   // au-delà : texte raccourci + « Lire la suite »

function raccourcir(texte: string) {
  if (texte.length <= MAX_CHARS) return texte
  const coupe = texte.slice(0, MAX_CHARS)
  return coupe.slice(0, coupe.lastIndexOf(' ')) + '…'
}

export default function TestimonialsCarouselClient({ testimonials, rating, count, mapsUrl }: Props) {
  const total = testimonials.length
  const [index, setIndex] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [paused, setPaused] = useState(false)
  const [reduceMotion, setReduceMotion] = useState(false)
  const [touchX, setTouchX] = useState<number | null>(null)

  useEffect(() => {
    setReduceMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  }, [])

  // Rotation automatique (en pause au survol, au toucher, texte déplié, ou animations réduites)
  useEffect(() => {
    if (paused || expanded || reduceMotion || total < 2) return
    const timer = setTimeout(() => setIndex((i) => (i + 1) % total), DELAY)
    return () => clearTimeout(timer)
  }, [index, paused, expanded, reduceMotion, total])

  if (total === 0) return null

  const aller = (sens: number) => {
    setExpanded(false)
    setIndex((i) => (i + sens + total) % total)
  }

  const t = testimonials[index]
  const long = t.text.length > MAX_CHARS
  const noteTexte = rating ? rating.toFixed(1).replace('.', ',') : ''

  return (
    <section className={s.section} aria-roledescription="carousel" aria-label="Avis">
      <div className={s.inner}>
        <div className={s.heading}>
          <span className={s.eyebrow}>Témoignages</span>
          <h2>
            Ils m’ont <em>fait confiance</em>
          </h2>

          {rating ? (
            <p className={s.rating}>
              <span className={s.stars} aria-hidden="true">★★★★★</span>{' '}
              {mapsUrl ? (
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                  {noteTexte} sur Google{count ? ` · ${count} avis` : ''}
                </a>
              ) : (
                <>{noteTexte} sur Google{count ? ` · ${count} avis` : ''}</>
              )}
            </p>
          ) : null}

          <p className={s.lead}>
            Des parents et des participants à mes conférences partagent leur expérience.
          </p>
        </div>

        <div className={s.carousel}>
          <div
            className={s.row}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={(e) => {
              setPaused(true)
              setTouchX(e.touches[0].clientX)
            }}
            onTouchEnd={(e) => {
              setPaused(false)
              if (touchX !== null) {
                const dx = e.changedTouches[0].clientX - touchX
                if (Math.abs(dx) > 50) aller(dx < 0 ? 1 : -1)
              }
              setTouchX(null)
            }}
          >
            <button type="button" className={s.arrow} aria-label="Avis précédent" onClick={() => aller(-1)}>‹</button>

            <article className={s.card}>
              <div className={s.quoteMark} aria-hidden="true">“</div>

              {t.source === 'google' && (
                <p className={s.badge}>
                  <span className={s.stars} aria-hidden="true">{'★'.repeat(t.rating ?? 5)}</span> Avis Google
                </p>
              )}

              <p className={s.text}>{expanded ? t.text : raccourcir(t.text)}</p>

              {long && (
                <button type="button" className={s.more} onClick={() => setExpanded((e) => !e)}>
                  {expanded ? 'Réduire' : 'Lire la suite'}
                </button>
              )}

              <div className={s.author}>
                <div className={s.avatar} aria-hidden="true">{t.author.charAt(0)}</div>
                <div>
                  <div className={s.authorName}>{t.author}</div>
                  {t.url && (
                    <a className={s.authorLink} href={t.url} target="_blank" rel="noopener noreferrer">
                      Voir sur Google
                    </a>
                  )}
                </div>
              </div>
            </article>

            <button type="button" className={s.arrow} aria-label="Avis suivant" onClick={() => aller(1)}>›</button>
          </div>

          <p className={s.counter}>{index + 1} / {total}</p>
        </div>
      </div>
    </section>
  )
}
